import React, { createContext, useContext, useState, ReactNode } from "react";
import {
  DisplayMode,
  DEFAULT_UI_SETTINGS,
  parseUISettings,
  formatUISettings,
} from "../utils/userPreferencesHelper";
import userPreferenceApiService from "../services/user-preference-api";

interface DisplayModeContextType {
  displayMode: DisplayMode;
  setDisplayMode: (mode: DisplayMode) => void;
  initializeFromPreferences: (settingsField: string | undefined | null) => void;
}

const DisplayModeContext = createContext<DisplayModeContextType | undefined>(
  undefined,
);

const DISPLAY_MODE_KEY = "display-mode";

export const DisplayModeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [displayMode, setDisplayModeState] = useState<DisplayMode>(() => {
    // Load display mode from localStorage
    const savedMode = localStorage.getItem(DISPLAY_MODE_KEY);
    return (savedMode as DisplayMode) || DEFAULT_UI_SETTINGS.displayMode;
  });

  const setDisplayMode = async (mode: DisplayMode) => {
    setDisplayModeState(mode);
    localStorage.setItem(DISPLAY_MODE_KEY, mode);

    // Save to backend
    try {
      // Get current preferences
      const currentPrefs = await userPreferenceApiService.getMyPreferences();

      // Parse current settings field to get dock position
      const uiSettings = parseUISettings(currentPrefs.settings);

      // Update display mode
      uiSettings.displayMode = mode;

      // Format and save
      const updatedSettingsField = formatUISettings(uiSettings);
      await userPreferenceApiService.saveMyPreferences({
        ...currentPrefs,
        settings: updatedSettingsField,
      });
    } catch (error) {
      console.error("Failed to save display mode to backend:", error);
      // Continue with localStorage-only fallback
    }
  };

  const initializeFromPreferences = (
    settingsField: string | undefined | null,
  ) => {
    const uiSettings = parseUISettings(settingsField);
    setDisplayModeState(uiSettings.displayMode);
    localStorage.setItem(DISPLAY_MODE_KEY, uiSettings.displayMode);
  };

  return (
    <DisplayModeContext.Provider
      value={{ displayMode, setDisplayMode, initializeFromPreferences }}
    >
      {children}
    </DisplayModeContext.Provider>
  );
};

export const useDisplayMode = (): DisplayModeContextType => {
  const context = useContext(DisplayModeContext);
  if (!context) {
    throw new Error("useDisplayMode must be used within a DisplayModeProvider");
  }
  return context;
};
